import React, { useMemo, useState, useEffect } from 'react';
import MuiPaper from '@mui/material/Paper';
import styled from 'styled-components/macro';
import { spacing } from '@mui/system';
import Feedback from '../../../../components/DataTable/Feedback';
import Stack from '@mui/material/Stack';
import {
  Typography,
  TableCell,
  useTheme,
  ThemeProvider,
  Divider as MuiDivider,
} from '@mui/material';
import shadows from '@mui/material/styles/shadows';
import DataTable from '../../../../components/DataTable';
import { useAuth } from '../../../../services/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { PAGE_SIZE } from '../../../../services/utils/units-constants';
import api from '../../../../Middleware/api/api';
import LogoLoader from '../../../../components/LogoLoader';
import { createTheme } from '@mui/material/styles';
const Paper = styled(MuiPaper)(spacing);

const dataTableTheme = (appTheme) =>
  createTheme({
    components: {
      MuiList: {
        styleOverrides: {
          root: {
            maxHeight: 300,
          },
        },
      },
      MuiPaper: {
        variants: [
          {
            props: {
              variant: 'elevation',
            },
            style: {
              boxShadow: shadows[3],
            },
          },
        ],
      },
      MUIDataTableFilter: {
        styleOverrides: {
          root: {
            width: 300,
            boxShadow: shadows[3],
            backgroundColor: appTheme.palette.background.paper,
          },
          title: {
            lineHeight: 3,
          },
          header: {
            marginBottom: 0,
          },
          gridListTile: {
            styleOverrides: {
              paddingLeft: '25px',
              marginLeft: '25px',
            },
            sx: {
              pl: 28,
            },
          },

          reset: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            paddingRight: 4,
          },
          resetLink: {
            button: {
              variants: [
                {
                  props: {
                    variant: 'outlined',
                  },
                },
              ],
            },
          },
        },
      },
      MUIDataTableHeadCell: {
        styleOverrides: {
          root: {
            paddingLeft: '8px',
            '&[data-colindex="5"], &[data-colindex="8"]': {
              '& > *': {
                display: 'flex',
                justifyContent: 'center',
                textAlign: 'center',
              },
              '& .MuiButton-root': {
                margin: 'auto',
              },
              '&[data-colindex="4"], &[data-colindex="1"], &[data-colindex="2"], &[data-colindex="3"], &[data-colindex="4", &[data-colindex="6"]':
                {
                  '& > *': {
                    display: 'flex',
                    justifyContent: 'left',
                    textAlign: 'left',
                  },
                  '& .MuiButton-root': {
                    margin: 'auto',
                    paddingLeft: '8px',
                  },
                },
              '&[data-colindex="7"]': {
                '& > *': {
                  display: 'flex',
                  justifyContent: 'right',
                  textAlign: 'right',
                },
                '& .MuiButton-root': {
                  margin: 'auto',
                },
              },
            },
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          variant: 'filled',
          margin: 'dense',
          size: 'medium',
          fullWidth: false,
          sx: {
            ml: 2,
            width: '230px',
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          margin: 'dense',
        },
      },
    },
  });
export function UsersTable({ customerId, hitGetDetails }) {
  const { t, i18n } = useTranslation();
  const { status, payment } = useTheme();
  const [selectedRows, setSelectedRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE[0]);
  const [fetchingData, setFetchingData] = React.useState(true);
  const [operatorContract, setOperatorContract] = React.useState([]);
  const { token } = useAuth();

  const getDashboardUsers = async () => {
    const urlParams = new URLSearchParams();
    urlParams.set('page', currentPage);
    urlParams.set('size', pageSize);

    setFetchingData(true);
    api
      .fetchdashboardusersbyid(token, customerId, urlParams)
      .then((response) => {
        if (response?.data?.data == null) {
          setOperatorContract([]);
          setFetchingData(false);
        } else {
          setTotalRecords(response?.data?.data?.totalNumberOfRecords);
          const rowsWithDetail = response?.data?.data?.response?.map((row) => ({
            ...row,
            details: row,
            name: row?.firstName + ' ' + row.lastName,
          }));
          setOperatorContract(rowsWithDetail);
          setFetchingData(false);
        }
      })
      .catch((error) => {
        setOperatorContract([]);
        setFetchingData(false);
      });
  };
  useEffect(() => {
    getDashboardUsers();
  }, [currentPage, pageSize, hitGetDetails]);

  useEffect(() => {
    setSelected(selectedRows.map((index) => operatorContract[index]));
  }, [selectedRows]);

  const [isOpen, setIsOpen] = useState(false);
  const [payload, setPayload] = useState([]);
  const [submit, setSubmit] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showErrorFeedback, setShowErrorFeedback] = useState(false);
  const [showNoEntriesFeedback, setShowNoEntriesFeedback] = useState(false);
  const [showNullBillingStatusFeedback, setShowNullBillingStatusFeedback] =
    useState(false);
  const [
    showMultiNullBillingStatusFeedback,
    setShowMultiNullBillingStatusFeedback,
  ] = useState(false);
  const [checkStatusChange, setCheckStatusChange] = useState(false);

  const [openElem, setOpenElem] = useState(null);
  const [anchorIconButton, setAnchorIconButton] = useState(null);
  const handleCloseIconMenu = () => {
    setOpenElem(null);
    setAnchorIconButton(null);
  };

  const columns = useMemo(() => [
    {
      name: 'customerId',
      options: {
        display: false,
        filter: false,
        customBodyRender: (value) => {
          return value || <>-</>;
        },
      },
    },

    {
      name: 'name',
      label: 'Name',
      options: {
        filter: false,
        customBodyRender: (value) => {
          return <Typography align="left">{value}</Typography>;
        },

        setCellHeaderProps: () => {
          return { align: 'start' };
        },
        setCellProps: () => {
          return { align: 'left' };
        },
      },
    },
    {
      name: 'email',
      label: 'Email',
      options: {
        filter: false,
        customBodyRender: (value) => {
          return <Typography align="left">{value}</Typography>;
        },
        customHeadRender: () => (
          <TableCell
            align="left"
            variant="head"
            sx={{
              textAlign: 'left',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
            }}
          >
            E-Mail
          </TableCell>
        ),
        setCellHeaderProps: () => {
          return { align: 'start' };
        },
        setCellProps: () => {
          return { align: 'left' };
        },
      },
    },
    {
      name: 'role',
      label: t('LBLRolle'),
      options: {
        filter: false,
        customBodyRender: (value) => {
          return <Typography align="left">{value}</Typography>;
        },
        customHeadRender: () => (
          <TableCell
            align="left"
            variant="head"
            sx={{
              textAlign: 'left',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
            }}
          >
            {t('LBLRolle')}
          </TableCell>
        ),
        setCellHeaderProps: () => {
          return { align: 'start' };
        },
        setCellProps: () => {
          return { align: 'left' };
        },
      },
    },
  ]);

  return (
    <>
      <LogoLoader loading={fetchingData} />
      <ThemeProvider
        theme={(appTheme) => ({
          ...appTheme,
          components: {
            ...appTheme.components,
            ...dataTableTheme(appTheme).components,
          },
        })}
      >
        <Stack
          alignItems="flex-end"
          spacing={8}
          position="relative"
          width={{
            sm: '100%',
            md: 'calc(100vw - 260px - 40px)',
            lg: 'calc(100vw - 260px - 64px)',
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              width: '100%',
              '& .MuiTableCell-head:not(:nth-of-type(2)) button': {
                mx: 0,
              },
            }}
          >
            <DataTable
              title={t('dsbuser')}
              data={operatorContract}
              columns={columns}
              options={{
                currentPage: currentPage,
                totalRecords: totalRecords,
                textLabels: {
                  body: {
                    noMatch: fetchingData ? t('LBLLOading') : t('LBLnoUsers'),
                  },
                },
                onRowSelectionChange: (_props, allRowsSelected) => {
                  setSelectedRows(
                    // eslint-disable-next-line no-unused-vars
                    allRowsSelected.map(({ index, dataIndex }) => dataIndex)
                  );
                },
                rowsSelected: selectedRows,
                filter: false,
                search: false,
                selectableRows: 'none',
                serverSide: true,
                count: totalRecords,
                rowsPerPage: pageSize,
                rowsPerPageOptions: PAGE_SIZE,
              }}
              getData={(seletedPageSize, currentPageVal) => {
                setCurrentPage(currentPageVal);
                setPageSize(seletedPageSize);
              }}
            />
          </Paper>
        </Stack>
      </ThemeProvider>
      <Feedback
        open={showFeedback}
        title={t('LBLPStatusChanged')}
        message={
          <Typography variant="subtitle1" sx={{ width: 400, mx: 10 }}>
            {t('paymentUpdated')}
          </Typography>
        }
        severity="warning"
        handleClose={() => {
          setPayload([]);
          setSelected([]);
          setShowFeedback(false);
        }}
      />
      <Feedback
        open={showErrorFeedback}
        message={
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ width: 400, mx: 10 }}
          >
            {t('LBLServerAgain')}
          </Typography>
        }
        severity="warning"
        handleClose={() => {
          setPayload([]);
          setSelected([]);
          setShowErrorFeedback(false);
        }}
      />
      <Feedback
        open={showNoEntriesFeedback}
        message={
          <Typography
            variant="subtitle1"
            fontWeight={400}
            sx={{ width: 400, mx: 10 }}
          >
            {t('LBLSelectOneUser')}
          </Typography>
        }
        severity="warning"
        handleClose={() => {
          setPayload([]);
          setSelected([]);
          setShowNoEntriesFeedback(false);
        }}
      />
      <Feedback
        open={showNullBillingStatusFeedback}
        title={t('LBLWrongSelection')}
        message={
          <Typography
            variant="subtitle1"
            fontWeight={400}
            sx={{ width: 400, mx: 10 }}
          >
            {t('LBLNoInvoice')}
          </Typography>
        }
        severity="warning"
        handleClose={() => {
          setPayload([]);
          setSelected([]);
          setShowNullBillingStatusFeedback(false);
        }}
      />
      <Feedback
        open={showMultiNullBillingStatusFeedback}
        title={t('LBLWrongSelection')}
        message={
          <Typography
            variant="subtitle1"
            fontWeight={400}
            sx={{ width: 400, mx: 10 }}
          >
            {t('LBLNoInvoiceUser')}
          </Typography>
        }
        severity="warning"
        handleClose={() => {
          setPayload([]);
          setSelected([]);
          setShowMultiNullBillingStatusFeedback(false);
        }}
      />
    </>
  );
}
